import L from 'leaflet';
import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer } from 'react-leaflet';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNavbar from '../../layouts/BottomNavbar';
import TopBar from '../../layouts/TopBar';
import MenuSidebar from '../../layouts/MenuSidebar';
import { darkTheme, lightTheme } from '../../theme/theme';
import useTheme from '../../hooks/useTheme';
import LoadingSpinner from '../../components/common/LoadingSpinner';

import { RouteFullModal, RouteOverlay } from '../../components/maps/RouteOverlay';
import { getActiveProduct, getProductDataFolder, PRODUCTS } from '../../utils/urlConfig';
import { fetchTemperatures } from '../../utils/weatherService';

const cartoKey = process.env.REACT_APP_CARTO_KEY;

const CCMapScreen = () => {
    const [isDark, setIsDark] = useTheme();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
    const [routePoints, setRoutePoints] = useState([]);
    const [pointsWithWeather, setPointsWithWeather] = useState([]);
    const [activeProduct, setActiveProductState] = useState('');
    const navigate = useNavigate();
    const { productId: urlProductId, serialNumber: urlSerialNumber } = useParams();

    const openRouteModal = () => {
        setIsRouteModalOpen(true);
        window.history.pushState({ mapModal: true }, '');
    };

    useEffect(() => {
        const handlePopState = () => {
            if (isRouteModalOpen) {
                setIsRouteModalOpen(false);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isRouteModalOpen]);

    const theme = isDark ? darkTheme : lightTheme;
    
    useEffect(() => {
        if (urlProductId && urlSerialNumber) {
            let product = getActiveProduct();
            for (const [key, config] of Object.entries(PRODUCTS)) {
                if (config.gtin === urlProductId) {
                    product = key;
                    break;
                }
            }
            setActiveProductState(product);
        } else {
            setActiveProductState(getActiveProduct());
        }
    }, [urlProductId, urlSerialNumber]);

    // Load route data
    useEffect(() => {
        const loadRouteData = async () => {
            try {
                const dataFolder = getProductDataFolder(activeProduct);
                const data = await import(`../../data/${dataFolder}/map_route.json`);
                const points = data.default || data;
                setRoutePoints(points);
                setPointsWithWeather(points);
            } catch (error) {
                console.error('Error loading route data:', error);
                setRoutePoints([]);
                setPointsWithWeather([]);
            }
        };
        loadRouteData();
    }, [activeProduct]);

    // Load weather data
    useEffect(() => {
        if (routePoints.length === 0) return;
        
        const loadWeather = async () => {
             const weatherData = await fetchTemperatures(routePoints);
             const updatedPoints = routePoints.map(point => {
                 const weather = weatherData.find(w => w.id === point.id);
                 return weather && weather.temperature ? { ...point, temperature: weather.temperature } : point;
             });
             setPointsWithWeather(updatedPoints);
        };
        loadWeather();
    }, [routePoints]);

    // Temperature coloring logic
    const temps = pointsWithWeather.map(s => parseFloat(s.temperature)).filter(n => !isNaN(n));
    const maxTemp = temps.length ? Math.max(...temps) : -Infinity;
    const minTemp = temps.length ? Math.min(...temps) : Infinity;

    const getTempColor = (tempStr) => {
        const t = parseFloat(tempStr);
        if (isNaN(t)) return '#3B82F6'; // Blue
        if (t === maxTemp) return '#EF4444'; // Red
        if (t === minTemp) return '#22C55E'; // Green
        return '#3B82F6'; // Blue
    };

     // Custom Icon Creator
        const createCustomIcon = (label) => {
            return L.divIcon({
                className: 'custom-marker-icon',
                html: `<span>${label}</span>`,
                iconSize: [24, 24],
                iconAnchor: [12, 12],
            });
        };

    return (
        <div
            className="flex flex-col h-screen w-full relative overflow-hidden"
            style={{ backgroundColor: theme.background1 }}
        >
            {/* Header */}
            <TopBar
                theme={theme}
                isDark={isDark}
                setIsDark={setIsDark}
                title="CC Map"
                onBackClick={() => {
                    const backPath = urlProductId && urlSerialNumber 
                        ? `/01/${urlProductId}/21/${urlSerialNumber}/dpp`
                        : `/01/${PRODUCTS[activeProduct]?.gtin}/21/${PRODUCTS[activeProduct]?.serialNumber}/dpp`;
                    navigate(backPath, { state: { isDark } });
                }}
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
                fixed={false}
            />

             {/* Menu Sidebar */}
            <MenuSidebar 
                theme={theme} 
                isDark={isDark} 
                isOpen={isMenuOpen} 
                onClose={() => setIsMenuOpen(false)} 
            />

            {/* Map */}
            <div className="flex-1 w-full relative z-0">
                {pointsWithWeather.length === 0 && <LoadingSpinner overlay />}
                <MapContainer
                    center={[26.5, 4.0]}
                    zoom={3}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={true}
                >
                    <TileLayer
                        attribution='&copy; OpenStreetMap contributors &copy; CARTO'
                        url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${cartoKey}`}
                    />

                    {pointsWithWeather.map((point, index) => (
                        <React.Fragment key={point.id}>
                            <Marker
                                position={point.position}
                                icon={createCustomIcon(point.label)}
                            />
                            {index < pointsWithWeather.length - 1 && (
                                <Polyline
                                    positions={[point.position, pointsWithWeather[index + 1].position]}
                                    pathOptions={{ 
                                        color: pointsWithWeather[index + 1].temperature ? getTempColor(pointsWithWeather[index + 1].temperature) : (pointsWithWeather[index + 1].pathColor || 'blue'), 
                                        weight: 3 
                                    }}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </MapContainer>

                {/* Route Overlay Card */}
                {!isRouteModalOpen && (
                <div className="fixed bottom-[100px] left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] z-[400] md:bottom-4">
                     <RouteOverlay 
                        stops={pointsWithWeather} 
                        isDark={isDark} 
                        onViewFull={() => openRouteModal()}
                        onViewGraph={() => {
                          const graphPath = urlProductId && urlSerialNumber
                            ? `/01/${urlProductId}/21/${urlSerialNumber}/temperature-graph`
                            : `/01/${PRODUCTS[activeProduct]?.gtin}/21/${PRODUCTS[activeProduct]?.serialNumber}/temperature-graph`;
                          navigate(graphPath, { state: { stops: pointsWithWeather, isDark } });
                        }}
                        showTemperature={true}
                     />
                </div>
                )}
            </div>

            <RouteFullModal 
                stops={pointsWithWeather} 
                isOpen={isRouteModalOpen} 
                onClose={() => window.history.back()} 
                isDark={isDark} 
                showTemperature={true}
            />

            {/* Spacer for navigation */}
            {!isRouteModalOpen && (
                <>
                <div className="flex-none h-[75px]" style={{ backgroundColor: theme.background1 }}></div>

                {/* Bottom Navigation */}
                <div className="absolute bottom-0 w-full z-50">
                    <BottomNavbar theme={theme} isDark={isDark} />
                </div>
                </>
            )}

        </div>
    );
};

export default CCMapScreen;